import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import T from 'components/T';
import { M } from '@dashboard-experience/mastodon';
import styled from 'styled-components';
import DownloadPDFNote from './DownloadPDFNote';
import { trackAnalyticsEvent } from '../../../actions';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from '../../../lib/analytics';
import CreditReportUtils from './CreditReportCommon/CreditReportUtils';
import { fetchReport } from '../../../api/verifications/client';
import { SESSION_STORAGE_TOKEN_KEY } from '../../../constants';

const SOCIAL_MEDIA_REPORT_FALLBACK_FILENAME = 'social-media-report.pdf';

const triggerPdfDownload = (downloadUri, filename) => {
  const anchor = document.createElement('a');
  anchor.href = downloadUri;
  anchor.download = filename;
  anchor.rel = 'noreferrer';
  anchor.target = '_blank';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const StyledPopoverContent = styled.div`
  .cds--popover-content {
    width: auto;
    max-width: fit-content;
    background-color: #fff;
  }
  .mastodon-button {
    font-weight: 400 !important;
  }
`;

const DownloadPDFDocuments = ({
  consentLink,
  documents,
  candidate,
  reportDownloadedAmplitudeEvent,
  report,
}) => {
  const [open, setOpen] = useState(false);

  /**
   *
   * @param {*} type [String] a type of document to find. Example: 'pdf_report'
   * @param {*} partial [Boolean] whether we include report PDFs with partial screenings in search.
   * Those documents are marked as "partial" in the API response.
   * As of now, partial documents can only be generated as a part of NYFC flow.
   * @returns [Object] the last document found or null if no document is found.
   */
  const find = (type, partial = false) => {
    let lastDocument = null;

    let filteredDocs = documents.filter(
      doc => Boolean(doc.partial) === partial,
    );

    if (!filteredDocs.length) {
      return null;
    }

    if (
      filteredDocs.length > 1 &&
      type === 'pdf_international_report' &&
      candidate.locale
    ) {
      filteredDocs = filteredDocs.filter(doc =>
        doc.locale?.startsWith(candidate.locale),
      );
    }

    filteredDocs.forEach(doc => {
      if (doc.type !== type) {
        return;
      }

      if (lastDocument === null || lastDocument.createdAt < doc.createdAt) {
        lastDocument = doc;
      }
    });

    return lastDocument;
  };

  const criminalReport =
    find('pdf_report_candidate') ||
    find('pdf_report_encrypted') ||
    find('pdf_report');

  const criminalPartialReport =
    find('pdf_report_candidate', true) ||
    find('pdf_report_encrypted', true) ||
    find('pdf_report', true);

  const drugAlcoholClearinghouseReport = find(
    'pdf_drug_alcohol_clearinghouse_report',
  );
  const internationalReport = find('pdf_international_report');
  const healthReport = find('pdf_health_report');
  const drugScreeningPDF = find('drug_screening_pdf');
  const occupationalHealthScreeningPDF = find(
    'occupational_health_screening_pdf',
  );

  const creditReport = find('pdf_credit_report');
  const selfDisclosureReport = find('pdf_self_disclosure');
  const paChildAbuseCertificate = find('pa_child_abuse_result_certificate');
  const socialMediaScreeningReport = report.socialMediaScreenings?.document;

  const creditReportUtils = new CreditReportUtils(report);

  const isInternationalHasNonEmptyCreditRecords =
    creditReportUtils.isInternationalHasNonEmptyRecords();

  const internationalCreditRecords =
    creditReportUtils.findInternationalRecords();

  const anyDoc =
    consentLink ||
    criminalReport ||
    criminalPartialReport ||
    drugAlcoholClearinghouseReport ||
    healthReport ||
    drugScreeningPDF ||
    occupationalHealthScreeningPDF ||
    creditReport ||
    paChildAbuseCertificate ||
    internationalReport ||
    isInternationalHasNonEmptyCreditRecords ||
    selfDisclosureReport ||
    socialMediaScreeningReport;

  if (!anyDoc) {
    return null;
  }

  const encrypted = !!(
    (criminalReport && criminalReport.type === 'pdf_report_encrypted') ||
    (criminalPartialReport &&
      criminalPartialReport.type === 'pdf_report_encrypted')
  );

  const downloadSocialMediaScreeningReport = async event => {
    event.preventDefault();

    if (!report.id) return;

    let freshSocialMediaReport = null;
    try {
      const response = await fetchReport({
        id: report.id,
        token: window.sessionStorage.getItem(SESSION_STORAGE_TOKEN_KEY),
      });
      if (response.data?.token) {
        window.sessionStorage.setItem(
          SESSION_STORAGE_TOKEN_KEY,
          response.data.token,
        );
      }
      freshSocialMediaReport =
        response.data?.report?.socialMediaScreenings?.document ||
        response.data?.report?.social_media_screenings?.document ||
        null;
    } catch {
      return;
    }

    const downloadUri =
      freshSocialMediaReport?.downloadUri ||
      freshSocialMediaReport?.download_uri;
    if (!downloadUri) return;

    triggerPdfDownload(
      downloadUri,
      freshSocialMediaReport.filename ||
        socialMediaScreeningReport.filename ||
        SOCIAL_MEDIA_REPORT_FALLBACK_FILENAME,
    );
    reportDownloadedAmplitudeEvent('Social Media Screening Report');
    setOpen(false);
  };

  return (
    <StyledPopoverContent>
      <M.Popover
        caret={false}
        data-testid='download-pdf-toggle'
        dropShadow
        id='download-pdf-toggle'
        open={open}
      >
        <M.Button kind='secondary' size='lg' onClick={() => setOpen(!open)}>
          {i18n.getStr('buttons.downloadPDF')}{' '}
          <M.Icon icon='CaretDown' size='16' />
        </M.Button>
        <M.PopoverContent style={{ width: 'inherit', 'max-width': 'inherit' }}>
          {consentLink && (
            <>
              <M.Link
                data-testid='download-consent'
                href={consentLink}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => {
                  setOpen(false);
                  reportDownloadedAmplitudeEvent(`Consent Link`);
                }}
              >
                <T value='buttons.downloadConsentFormPDF' />
              </M.Link>
              {(criminalReport ||
                criminalPartialReport ||
                drugAlcoholClearinghouseReport ||
                healthReport ||
                internationalReport ||
                creditReport ||
                paChildAbuseCertificate ||
                selfDisclosureReport ||
                socialMediaScreeningReport) && <M.Divider />}
            </>
          )}
          {criminalReport && (
            <M.Link
              data-testid='download-report'
              href={criminalReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Criminal Report`);
              }}
            >
              <M.Button kind='tertiary' size='md'>
                <T value='buttons.downloadCriminalReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {criminalPartialReport && (
            <M.Link
              data-testid='download-partial-report'
              href={criminalPartialReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Criminal Partial Report`);
              }}
            >
              <M.Button kind='tertiary' size='md'>
                <T value='buttons.downloadCriminalReportPDFPartial' />
              </M.Button>
            </M.Link>
          )}
          {drugAlcoholClearinghouseReport && (
            <M.Link
              data-testid='download-dac-report'
              href={drugAlcoholClearinghouseReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`DAC Report`);
              }}
            >
              <M.Button kind='tertiary' size='md'>
                <T value='buttons.downloadDrugAlcoholClearinghouseReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {healthReport && (
            <M.Link
              data-testid='health-report'
              href={healthReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Health Report`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadHealthReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {drugScreeningPDF && (
            <M.Link
              data-testid='drug-screening-pdf'
              href={drugScreeningPDF.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Drug Screening PDF`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadDrugScreeningPDF' />
              </M.Button>
            </M.Link>
          )}
          {occupationalHealthScreeningPDF && (
            <M.Link
              data-testid='occupational-health-screening-pdf'
              href={occupationalHealthScreeningPDF.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(
                  `Occupational Heatlh Screening PDF`,
                );
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadOccupationalHealthScreeningPDF' />
              </M.Button>
            </M.Link>
          )}
          {internationalReport && (
            <M.Link
              href={internationalReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`International Report`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadInternationalReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {creditReport && (
            <M.Link
              href={creditReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Credit Report`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadCreditReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {isInternationalHasNonEmptyCreditRecords && (
            <M.Link
              href={internationalCreditRecords[0].pdfUrl}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Credit Report`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadCreditReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {paChildAbuseCertificate && (
            <M.Link
              href={paChildAbuseCertificate.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Child Abuse Certificate`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadPaChildAbuseCertificate' />
              </M.Button>
            </M.Link>
          )}
          {selfDisclosureReport && (
            <M.Link
              href={selfDisclosureReport.downloadUri}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                reportDownloadedAmplitudeEvent(`Self Disclosure Report`);
              }}
            >
              <M.Button kind='tertiary'>
                <T value='buttons.downloadSelfDisclosureReportPDF' />
              </M.Button>
            </M.Link>
          )}
          {socialMediaScreeningReport && (
            <M.Link
              data-testid='social-media-screening-pdf'
              href='#'
              onClick={downloadSocialMediaScreeningReport}
            >
              <T value='buttons.downloadSocialMediaScreeningPDF' />
            </M.Link>
          )}
        </M.PopoverContent>
      </M.Popover>
      <DownloadPDFNote encryption={encrypted} />
    </StyledPopoverContent>
  );
};

DownloadPDFDocuments.propTypes = {
  consentLink: PropTypes.string,
  documents: PropTypes.array,
  candidate: PropTypes.object,
  reportDownloadedAmplitudeEvent: PropTypes.func,
  report: PropTypes.object,
};

DownloadPDFDocuments.defaultProps = {
  consentLink: '',
  documents: [],
  candidate: {},
  reportDownloadedAmplitudeEvent: () => {},
  report: {},
};

const mapDispatchToProps = {
  reportDownloadedAmplitudeEvent: reportName =>
    trackAnalyticsEvent(ANALYTICS_EVENTS.REPORT_DOWNLOADED, {
      [ANALYTICS_PROPERTIES.REPORT_NAME]: reportName,
    }),
};
export default connect(null, mapDispatchToProps)(DownloadPDFDocuments);
